import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { hoverable } from '../../interaction-states';
import { typographyVariantStyles } from '../../typography-variants';

const TRANSIENT_PROPS = new Set(['$isDraggingOver', '$disabled', '$isError']);

function getBorderColor(theme: Theme, isDraggingOver: boolean, isError: boolean): string {
  if (isDraggingOver) return theme.colors.border.focus;
  if (isError) return theme.colors.error.border;
  return theme.colors.border.default;
}

export const StyledRoot = styled('label', {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop as string),
})<{ $isDraggingOver: boolean; $disabled: boolean; $isError: boolean }>(
  ({ theme, $isDraggingOver, $disabled, $isError }) => ({
    ...(!$isDraggingOver && hoverable(theme, 'surfaceRaised')),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing[16],
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing[16],
    borderRadius: theme.radius[16],
    border: `2px dashed ${getBorderColor(theme, $isDraggingOver, $isError)}`,
    backgroundColor: $isDraggingOver ? theme.colors.brand.subtle : theme.colors.background.raised,
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.5 : 1,
    pointerEvents: $disabled ? 'none' : 'auto',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  }),
);

export const StyledIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  color: theme.colors.icon.primary,

  '& svg': {
    width: 24,
    height: 24,
  },
}));

export const StyledTextContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
});

export const StyledPrompt = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,
}));

export const StyledErrorMessage = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.error,
}));

export const StyledHint = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,
}));

export const StyledHiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
